import { createContext, useState } from 'react';
import { ContactPeople } from '../../../../api/service/superAdmin/company-clients/types';
import {
  INITIAL_COMPANY_DETAILS,
  INITIAL_ADDRESS_DETAILS,
  INITIAL_SIGNATORY_DETAILS,
  INITIAL_CONTACT_PEOPLE,
  FORM_INITIAL_VALUES,
  INITIAL_INIVITATION_EMAIL_SETTING,
} from '../contants';
import {
  CollectiveAgreementType,
  CompanyDetails,
  AddressDetails,
  AuthorizedSignatory,
  AdminDetails,
  ICompanyClientContext,
  Consultants,
  AccountingFirm,
  AuthorizedSignatoryGroup,
  CompanyClientFormGroup,
  Deployment,
  IntegrationSystem,
  IntegrationSystemPartial,
  AuthorizedSignatoryOptionItem,
} from '../types';
import { formSchema } from './schema';
import { useFormik } from 'formik';

export const CreateCompanyClientContext = createContext<ICompanyClientContext>({
  companyDetails: INITIAL_COMPANY_DETAILS,
  deployment: { systemDeploymentMonth: null , employeeInvitationSendOuts: null },
  setDeployment: () => {},
  updateDeployment: () => {},

  updateIntegration: () => {},
  replaceIntegration: () => {},

  setCompanyDetails: () => {},

  addressDetails: INITIAL_ADDRESS_DETAILS,
  setAddressDetails: () => {},

  adminDetails: [],
  setAdminDetails: () => {},

  signatoryDetails: [],
  setSignatoryDetails: () => {},

  signatoryAdmins: [],
  setSignatoryAdmins: () => {},

  deletedAdmins: [],
  setDeletedAdmins: () => {},

  deletedConsultants: [],
  setDeletedConsultants: () => {},

  combinations: [],
  setCombinations: () => {},

  salaryContactPeople: INITIAL_CONTACT_PEOPLE,
  setSalaryContactPeople: () => {},

  accountingContactPeople: INITIAL_CONTACT_PEOPLE,
  setAccountingContactPeople: () => {},

  contractEffectiveFrom: '',
  setContractEffectiveFrom: () => {},

  invoiceCostCenter: '',
  setInvoiceCostCenter: () => {},

  contractEmail: '',
  setContractEmail: () => {},

  contractNumber: '',
  setContractNumber: () => {},

  collectiveAgreements: [],
  setCollectiveAgreements: () => {},

  consultants: [],
  setConsultants: () => {},

  accountingFirms: [],
  setAccountingFirms: () => {},

  invitationEmail: INITIAL_INIVITATION_EMAIL_SETTING,
  setInvitationEmail: () => {},

  isEditMode: false,

  formGroup: {} as CompanyClientFormGroup,

  authorizedSignatoryOptions: [],
  setAuthorizedSignatoryOptions: () => {},

  replaceAdminDetails: () => {},
  replaceSignatoryAdmins: () => {},
  appendDeletedAdmin: () => {},
  appendDeletedConsultant: () => {},
  replaceAddress: () => {},
  replaceAuthorizedSignatory: () => {},
  replaceCombinations: () => {},
  replaceCompanyDetails: () => {},
  replaceConsultants: () => {},
  replaceConsultant: () => {},
  replaceAccountingContactPeople: () => {},
  replaceSalaryContactPeople: () => {},
  updateAdminDetails: () => {},
  updateAddress: () => {},
  updateAuthorizedSignatory: () => {},
  updateCombinations: () => {},
  updateCompanyDetails: () => {},
  updateConsultants: () => {},
  updateConsultant: () => {},
  updateAccountingContactPeople: () => {},
  updateSalaryContactPeople: () => {},
  updateContractEffectiveInput: () => {},
  updateInvoicingCostCenter: () => {},
  updateInvoiceEmailAddress: () => {},
  updateContractNumber: () => {},
  replaceCollectiveAgreements: () => {},
  updateInvitationEmail: () => {},
  replaceAccessLevel: () => {},
  replaceDeployment: (deployment: Deployment) => {},
});

type Props = {
  children: JSX.Element;
};

export const CreateCompanyClientProvider = ({ children }: Props) => {
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>(INITIAL_COMPANY_DETAILS);
  const [deployment, setDeployment] = useState<{ systemDeploymentMonth: string | null; employeeInvitationSendOuts: string | null }>({ systemDeploymentMonth: null, employeeInvitationSendOuts: null });
  const [addressDetails, setAddressDetails] = useState<AddressDetails>(INITIAL_ADDRESS_DETAILS);
  const [adminDetails, setAdminDetails] = useState<Array<AdminDetails>>([]);
  const [signatoryAdmins, setSignatoryAdmins] = useState<Array<AuthorizedSignatory>>([]);
  const [deletedAdmins, setDeletedAdmins] = useState<Array<string>>([]);
  const [deletedConsultants, setDeletedConsultants] = useState<Array<string>>([]);
  const [signatoryDetails, setSignatoryDetails] = useState<Array<AuthorizedSignatory>>([
    { _id: `${Math.random()}`, ...INITIAL_SIGNATORY_DETAILS, incharge: true },
    { _id: `${Math.random()}`, ...INITIAL_SIGNATORY_DETAILS },
  ]);
  const [combinations, setCombinations] = useState<Array<AuthorizedSignatoryGroup>>([]);
  const [salaryContactPeople, setSalaryContactPeople] = useState<ContactPeople>(INITIAL_CONTACT_PEOPLE);
  const [accountingContactPeople, setAccountingContactPeople] = useState<ContactPeople>(INITIAL_CONTACT_PEOPLE);
  const [contractEffectiveFrom, setContractEffectiveFrom] = useState<string>('');
  const [invoiceCostCenter, setInvoiceCostCenter] = useState<string>('');
  const [contractEmail, setContractEmail] = useState<string>('');
  const [contractNumber, setContractNumber] = useState<string>('');
  const [collectiveAgreements, setCollectiveAgreements] = useState<Array<CollectiveAgreementType>>([]);
  const [consultants, setConsultants] = useState<Array<Consultants>>([]);
  const [accountingFirms, setAccountingFirms] = useState<Array<AccountingFirm>>([]);
  const [invitationEmail, setInvitationEmail] = useState<string>(INITIAL_INIVITATION_EMAIL_SETTING);
  const [authorizedSignatoryOptions, setAuthorizedSignatoryOptions] = useState<Array<AuthorizedSignatoryOptionItem[]>>([]);
  const isEditMode = false;

  const formGroup = useFormik({
    initialValues: FORM_INITIAL_VALUES,
    validateOnMount: true,
    validationSchema: formSchema,
    onSubmit: () => {},
  });

  const replaceAdminDetails = (value) => {
    setAdminDetails(value);
    formGroup.setFieldValue('admins', value);
  };

  const replaceSignatoryAdmins = (value) => {
    setSignatoryAdmins(value);
    formGroup.setFieldValue('signatoryAdmins', value);
  };

  const replaceAddress = (value) => {
    setAddressDetails(value);
    formGroup.setFieldValue('address', value);
    formGroup.setFieldTouched('address');
  };

  const replaceAuthorizedSignatory = (value) => {
    setSignatoryDetails(value);
    formGroup.setFieldValue('authorizedSignatory', value);
  };

  const replaceCombinations = (value) => {
    setCombinations(value);
    formGroup.setFieldValue('combinations', value);
  };

  const replaceCompanyDetails = (value) => {
    setCompanyDetails(value);
    formGroup.setFieldValue('companyDetails', value);
    formGroup.setFieldTouched('companyDetails');
  };

  const replaceConsultants = (value) => {
    setConsultants(value);
    formGroup.setFieldValue('consultants', value);
  };
  const replaceConsultant = (value) => {
    formGroup.setFieldValue('consultant', value);
  };
  const replaceAccountingContactPeople = (value) => {
    setAccountingContactPeople(value);
    formGroup.setFieldValue('accountingContactPeople', value);
  };

  const updateIntegration = (inputName: keyof IntegrationSystem, inputValue: string | null) => {
    formGroup.setFieldValue(`integration.${inputName}`, inputValue);
  };

  const replaceIntegration = (integrationObj: IntegrationSystemPartial) => {
    formGroup.setFieldValue('integration', integrationObj);
  };

  const replaceSalaryContactPeople = (value) => {
    setSalaryContactPeople(value);
    formGroup.setFieldValue('salaryContactPeople', value);
  };

  const appendDeletedAdmin = (value) => {
    if (!isEditMode) {
      return;
    }

    const _deletedAdmins = [...deletedAdmins];
    _deletedAdmins.push(value);

    setDeletedAdmins(_deletedAdmins);
  };

  const appendDeletedConsultant = (value) => {
    if (!isEditMode) {
      return;
    }

    const _deletedConsultants = [...deletedConsultants];
    _deletedConsultants.push(value);

    setDeletedConsultants(_deletedConsultants);
  };

  const updateAddress = (inputName, inputValue) => {
    setAddressDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`address.${inputName}`, inputValue);
  };

  const updateAdminDetails = (inputName, inputValue) => {
    setAdminDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`admins.${inputName}`, inputValue);
  };

  const updateAuthorizedSignatory = (inputName, inputValue) => {
    setSignatoryDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`authorizedSignatory.${inputName}`, inputValue);
  };

  const updateCombinations = (inputName, inputValue) => {
    setCombinations((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`combinations.${inputName}`, inputValue);
  };

  const updateCompanyDetails = (inputName, inputValue) => {
    setCompanyDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`companyDetails.${inputName}`, inputValue);
  };

  const updateConsultants = (inputName, inputValue) => {
    setConsultants((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`consultants.${inputName}`, inputValue);
  };

  const updateConsultant = (inputName, inputValue) => {
    formGroup.setFieldValue(`consultant.${inputName}`, inputValue);
  };

  const updateAccountingContactPeople = (inputName, inputValue) => {
    setAccountingContactPeople((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`accountingContactPeople.${inputName}`, inputValue);
  };

  const updateSalaryContactPeople = (inputName, inputValue) => {
    setSalaryContactPeople((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`salaryContactPeople.${inputName}`, inputValue);
  };

  const updateContractEffectiveInput = (inputValue) => {
    setContractEffectiveFrom(inputValue);
    formGroup.setFieldValue(`contractEffectiveFrom`, inputValue);
  };

  const updateInvoicingCostCenter = (inputValue) => {
    setInvoiceCostCenter(inputValue);
    formGroup.setFieldValue(`invoiceCostCenter`, inputValue);
  };

  const updateInvoiceEmailAddress = (inputValue) => {
    setContractEmail(inputValue);
    formGroup.setFieldValue(`contractEmail`, inputValue);
  };

  const updateContractNumber = (inputValue) => {
    setContractNumber(inputValue);
    formGroup.setFieldValue(`contractNumber`, inputValue);
  };

  const replaceCollectiveAgreements = (inputValue) => {
    setCollectiveAgreements(inputValue);
    formGroup.setFieldValue('collectiveAgreements', inputValue);
  };

  const updateInvitationEmail = (inputValue) => {
    setInvitationEmail(inputValue);
    formGroup.setFieldValue(`invitationEmail`, inputValue);
  };

  const replaceAccessLevel = (allowRCorrectoAccess, allowAdvisorAccess) => {
    formGroup.setFieldValue(`allowCorrectoAccess`, allowRCorrectoAccess);
    formGroup.setFieldValue(`allowAdvisorAccess`, allowAdvisorAccess);
  };

  const updateDeployment = (inputName: keyof typeof deployment, inputValue: string | null) => {
    setDeployment((prev) => ({
      ...prev,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`deployment.${inputName}`, inputValue);
  };

  const replaceDeployment = (deployment: Deployment) => {
    setDeployment(deployment);
    formGroup.setFieldValue('deployment', deployment);
  };

  const context: ICompanyClientContext = {
    companyDetails,
    setCompanyDetails,
    deployment,
    setDeployment,
    updateDeployment,
    replaceDeployment,
    updateIntegration,
    replaceIntegration,
    adminDetails,
    setAdminDetails,
    signatoryDetails,
    setSignatoryDetails,
    signatoryAdmins,
    setSignatoryAdmins,
    deletedAdmins,
    setDeletedAdmins,
    deletedConsultants,
    setDeletedConsultants,
    combinations,
    setCombinations,
    addressDetails,
    setAddressDetails,
    salaryContactPeople,
    setSalaryContactPeople,
    accountingContactPeople,
    setAccountingContactPeople,
    contractEffectiveFrom,
    setContractEffectiveFrom,
    collectiveAgreements,
    setCollectiveAgreements,
    invoiceCostCenter,
    setInvoiceCostCenter,
    contractEmail,
    setContractEmail,
    consultants,
    setConsultants,
    accountingFirms,
    setAccountingFirms,
    invitationEmail,
    setInvitationEmail,
    contractNumber,
    setContractNumber,
    isEditMode: false,
    formGroup: formGroup as any,
    authorizedSignatoryOptions,
    setAuthorizedSignatoryOptions,
    replaceAdminDetails,
    replaceSignatoryAdmins,
    replaceAddress,
    replaceAuthorizedSignatory,
    replaceCombinations,
    replaceCompanyDetails,
    replaceConsultants,
    replaceConsultant,
    replaceAccountingContactPeople,
    replaceSalaryContactPeople,
    appendDeletedAdmin,
    appendDeletedConsultant,
    updateAdminDetails,
    updateAddress,
    updateAuthorizedSignatory,
    updateCombinations,
    updateCompanyDetails,
    updateConsultants,
    updateConsultant,
    updateAccountingContactPeople,
    updateSalaryContactPeople,
    updateContractEffectiveInput,
    updateInvoiceEmailAddress,
    updateContractNumber,
    updateInvoicingCostCenter,
    replaceCollectiveAgreements,
    updateInvitationEmail,
    replaceAccessLevel,
  };

  return <CreateCompanyClientContext.Provider value={context}>{children}</CreateCompanyClientContext.Provider>;
};
